import { supabase } from '@/lib/supabase/client';
import { normalizeEmail } from '@/lib/contact-vault/normalize';
import { formatClosedLostDeleteError, isMissingDeleteRpc } from '@/lib/deals/closed-lost-contact';
import { type NetworkPulseDealEngagement } from '@/lib/deals/pulse-engagement';
import { type PulseEngagementScore } from '@/lib/deals/pulse-engagement-score';
import { isTrackBDeal } from '@/lib/outreach/nurture-draft';
import { buildNurtureUpdatePayload } from '@/lib/prospect-nurture/helpers';
import type {
  Deal,
  DealInsert,
  DealUpdate,
  DealFlag,
  DealFlagType,
  DealLocationConversionInput,
  DealLocationConversionResult,
  SalesState,
} from '@/types/deals';
import { validateTransition, computeDerivedFields } from '../deal-workflow';
import { locationOrdersService } from './location-orders';

function formatDealPosSystemSchemaError(message: string): string {
  if (!message.includes('pos_system')) {
    return message;
  }

  return 'Failed to save deal POS. The current Supabase project does not have deals.pos_system available yet. Run the deal POS schema migration against the live app database and refresh the schema cache, then try again.';
}

// Helper to get current user ID
async function getCurrentUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.id ?? null;
}

async function moveDealDocumentsToLocation(dealId: string, locationId: string): Promise<void> {
  const { error: moveError } = await supabase
    .from('deal_documents')
    .update({ location_id: locationId })
    .eq('deal_id', dealId)
    .is('location_id', null);

  if (moveError) {
    throw new Error(`Failed to move deal documents: ${moveError.message}`);
  }

  const { data: location, error: locationError } = await supabase
    .from('locations')
    .select('contract_file_path, contract_file_url')
    .eq('id', locationId)
    .maybeSingle();

  if (locationError || !location || location.contract_file_path || location.contract_file_url) {
    return;
  }

  const { data: documents, error: documentsError } = await supabase
    .from('deal_documents')
    .select('storage_path')
    .eq('deal_id', dealId)
    .eq('location_id', locationId)
    .in('document_type', ['contract', 'sla'])
    .order('created_at', { ascending: false })
    .limit(1);

  if (documentsError) {
    throw new Error(`Failed to read moved deal documents: ${documentsError.message}`);
  }

  const storagePath = (documents as Array<{ storage_path: string }> | null)?.[0]?.storage_path;
  if (!storagePath) {
    return;
  }

  const { error: updateLocationError } = await supabase
    .from('locations')
    .update({ contract_file_path: storagePath, contract_file_url: null })
    .eq('id', locationId);

  if (updateLocationError) {
    throw new Error(`Failed to attach moved contract to location: ${updateLocationError.message}`);
  }
}

export const dealsService = {
  async convertToLocation(input: DealLocationConversionInput): Promise<DealLocationConversionResult> {
    const { data, error } = await supabase
      .rpc('convert_deal_to_location', {
        p_deal_id: input.dealId,
        p_location_number: input.locationNumber,
        p_location_name: input.locationName,
        p_location_status: input.locationStatus,
        p_address: input.address ?? null,
        p_contract_signed_date: input.contractSignedDate ?? null,
        p_contract_renewal_date: input.contractRenewalDate ?? null,
        p_contract_term_months: input.contractTermMonths ?? null,
        p_contract_signee_name: input.contractSigneeName ?? null,
        p_contract_signee_title: input.contractSigneeTitle ?? null,
        p_installation_date: input.installationDate ?? null,
        p_launch_date: input.launchDate ?? null,
        p_monthly_license_fee: input.monthlyLicenseFee ?? null,
        p_license_fee_period: input.licenseFeePeriod ?? null,
        p_pos_system: input.posSystem ?? null,
      })
      .single();

    if (error) {
      throw new Error(`Failed to convert deal: ${error.message}`);
    }

    const result = data as DealLocationConversionResult;
    if (!result.ok) {
      throw new Error(result.error_message || 'Deal conversion failed.');
    }

    if (result.location_id) {
      try {
        await moveDealDocumentsToLocation(input.dealId, result.location_id);
      } catch (error) {
        console.error('Error moving deal documents after conversion:', error);
      }
    }

    return result;
  },

  async linkToExistingLocation(dealId: string, locationId: string): Promise<Deal> {
    const deal = await this.getById(dealId);
    if (!deal) {
      throw new Error('Deal not found');
    }
    if (deal.location_id) {
      throw new Error('This deal is already linked to a location.');
    }

    const { data: location, error: locationError } = await supabase
      .from('locations')
      .select('id')
      .eq('id', locationId)
      .maybeSingle();

    if (locationError) {
      throw new Error(`Failed to verify location: ${locationError.message}`);
    }
    if (!location) {
      throw new Error('Selected location was not found.');
    }

    const updatedDeal = await this.update(dealId, {
      location_id: locationId,
      stage: 'closed_won',
      sales_state: 4,
      nurture_stage: 'exited_won',
    });

    try {
      await moveDealDocumentsToLocation(dealId, locationId);
    } catch (error) {
      console.error('Error moving deal documents after linking location:', error);
    }

    return updatedDeal;
  },

  async getAll(): Promise<Deal[]> {
    try {
      const { data, error } = await supabase.from('deals').select('*').order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching deals:', error);
        return [];
      }

      // Transform data to ensure flags are properly parsed
      const deals = (data || []).map((deal: any) => ({
        ...deal,
        flags: Array.isArray(deal.flags) ? deal.flags : (deal.flags ? JSON.parse(deal.flags) : []),
        sales_state: deal.sales_state ?? 1, // Default to 1 if null
      }));

      return deals as Deal[];
    } catch (error) {
      console.error('Error fetching deals:', error);
      return [];
    }
  },

  /**
   * Normalized emails owned by Track B nurture deals.
   * Used to keep those contacts out of the Track A week queue.
   */
  async listNurtureTrackEmails(): Promise<Set<string>> {
    const owners = await this.listNurtureTrackOwners();
    return new Set(owners.keys());
  },

  /** Email → nurture deal owner for Prospecting ↔ Track B reconcile. */
  async listNurtureTrackOwners(): Promise<Map<string, { dealId: string; dealName: string; email: string }>> {
    try {
      const { data, error } = await supabase
        .from('deals')
        .select('id,name,stage,prospect_contact_email,nurture_opt_out,updated_at,created_at')
        .in('stage', ['closed_lost', 'proposal_sent', 'qualified'])
        .not('prospect_contact_email', 'is', null);

      if (error) {
        console.error('Error fetching nurture track owners:', error);
        throw new Error(`Failed to fetch nurture track owners: ${error.message}`);
      }

      const owners = new Map<string, { dealId: string; dealName: string; email: string }>();
      for (const row of data ?? []) {
        const deal = {
          ...row,
          nurture_opt_out: Boolean(row.nurture_opt_out),
          updated_at: row.updated_at ?? row.created_at ?? new Date().toISOString(),
        } as Deal;
        if (!isTrackBDeal(deal)) continue;
        const email = normalizeEmail(deal.prospect_contact_email);
        if (!email || owners.has(email)) continue;
        owners.set(email, {
          dealId: deal.id,
          dealName: deal.name,
          email,
        });
      }
      return owners;
    } catch (error) {
      console.error('Error listing nurture track owners:', error);
      throw error instanceof Error
        ? error
        : new Error('Failed to list nurture track owners');
    }
  },

  async getById(id: string): Promise<Deal | null> {
    try {
      const { data, error } = await supabase.from('deals').select('*').eq('id', id).single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching deal:', error);
        return null;
      }

      if (!data) return null;

      // Transform data to ensure flags are properly parsed
      const deal = {
        ...data,
        flags: Array.isArray(data.flags) ? data.flags : (data.flags ? JSON.parse(data.flags) : []),
        sales_state: data.sales_state ?? 1, // Default to 1 if null
      };

      return deal as Deal;
    } catch (error) {
      console.error('Error fetching deal:', error);
      return null;
    }
  },

  async getByLocationId(locationId: string): Promise<Deal[]> {
    try {
      const { data, error } = await supabase
        .from('deals')
        .select('*')
        .eq('location_id', locationId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching deals by location ID:', error);
        return [];
      }

      // Transform data to ensure flags are properly parsed
      const deals = (data || []).map((deal: any) => ({
        ...deal,
        flags: Array.isArray(deal.flags) ? deal.flags : (deal.flags ? JSON.parse(deal.flags) : []),
        sales_state: deal.sales_state ?? 1, // Default to 1 if null
      }));

      return deals as Deal[];
    } catch (error) {
      console.error('Error fetching deals by location ID:', error);
      return [];
    }
  },

  async create(data: DealInsert): Promise<Deal> {
    try {
      if (data.stage === 'closed_won' && !data.location_id) {
        throw new Error('Cannot create a closed-won prospect without a location. Create the location during deal conversion first.');
      }

      const userId = await getCurrentUserId();

      const insertData: Record<string, unknown> = {
        location_id: data.location_id ?? null, // null for prospects
        contact_id: data.contact_id ?? null,
        name: data.name,
        address: data.address ?? null,
        pos_system: data.pos_system?.trim() || null,
        stage: data.stage ?? 'lead',
        total_contract_value: data.total_contract_value,
        upfront_amount: data.upfront_amount ?? null,
        recurring_amount: data.recurring_amount ?? null,
        recurring_type: data.recurring_type ?? null,
        estimated_start_date: data.estimated_start_date ?? null,
        source: data.source ?? null,
        owner_id: data.owner_id ?? null,
        notes: data.notes ?? null,
        prospect_contact_name: data.prospect_contact_name ?? null,
        prospect_contact_email: data.prospect_contact_email ?? null,
        prospect_contact_phone: data.prospect_contact_phone ?? null,
        prospect_contact_role: data.prospect_contact_role ?? null,
        created_by: userId,
      };

      // Add workflow fields if provided (only if migration has been run)
      // These fields may not exist in the database yet, so we check if they're provided
      if (data.mode !== undefined) insertData.mode = data.mode;
      if (data.sales_state !== undefined) {
        insertData.sales_state = data.sales_state;
      } else if (data.stage) {
        // Default sales_state based on stage (only if stage is provided)
        const stageToState: Record<string, SalesState> = {
          lead: 1,
          qualified: 2,
          proposal_sent: 2,
          verbal_yes: 3,
          closed_won: 4,
          closed_lost: 6,
        };
        const defaultState = stageToState[data.stage] ?? 1;
        // Only set if we have a valid stage
        if (defaultState) insertData.sales_state = defaultState;
      }
      if (data.flags !== undefined) insertData.flags = JSON.stringify(data.flags);
      if (data.closed_reason !== undefined) insertData.closed_reason = data.closed_reason;
      Object.assign(insertData, buildNurtureUpdatePayload(data));

      const { data: newDeal, error } = await supabase.from('deals').insert(insertData).select().single();

      if (error) {
        console.error('Error creating deal:', error);
        throw new Error(formatDealPosSystemSchemaError(`Failed to create deal: ${error.message}`));
      }

      return newDeal as Deal;
    } catch (error) {
      console.error('Error creating deal:', error);
      throw error;
    }
  },

  async update(id: string, data: DealUpdate): Promise<Deal> {
    try {
      // Get current deal to check for stage change
      const currentDeal = await this.getById(id);
      if (!currentDeal) {
        throw new Error('Deal not found');
      }

      const wasClosedWon = currentDeal.stage === 'closed_won';
      const willBeClosedWon = data.stage === 'closed_won';

      if (!currentDeal.location_id && willBeClosedWon && !data.location_id) {
        throw new Error('Cannot mark a prospect as closed won without creating or linking a location first.');
      }

      const updateData: Record<string, unknown> = {};

      // Only include fields that are provided
      if (data.location_id !== undefined) updateData.location_id = data.location_id;
      if (data.contact_id !== undefined) updateData.contact_id = data.contact_id;
      if (data.name !== undefined) updateData.name = data.name;
      if (data.address !== undefined) updateData.address = data.address;
      if (data.pos_system !== undefined) updateData.pos_system = data.pos_system?.trim() || null;
      if (data.stage !== undefined) updateData.stage = data.stage;
      if (data.total_contract_value !== undefined) updateData.total_contract_value = data.total_contract_value;
      if (data.upfront_amount !== undefined) updateData.upfront_amount = data.upfront_amount;
      if (data.recurring_amount !== undefined) updateData.recurring_amount = data.recurring_amount;
      if (data.recurring_type !== undefined) updateData.recurring_type = data.recurring_type;
      if (data.estimated_start_date !== undefined) updateData.estimated_start_date = data.estimated_start_date;
      if (data.source !== undefined) updateData.source = data.source;
      if (data.owner_id !== undefined) updateData.owner_id = data.owner_id;
      if (data.notes !== undefined) updateData.notes = data.notes;
      if (data.prospect_contact_name !== undefined) updateData.prospect_contact_name = data.prospect_contact_name;
      if (data.prospect_contact_email !== undefined) updateData.prospect_contact_email = data.prospect_contact_email;
      if (data.prospect_contact_phone !== undefined) updateData.prospect_contact_phone = data.prospect_contact_phone;
      if (data.prospect_contact_role !== undefined) updateData.prospect_contact_role = data.prospect_contact_role;
      // Workflow fields
      if (data.mode !== undefined) updateData.mode = data.mode;
      if (data.sales_state !== undefined) updateData.sales_state = data.sales_state;
      if (data.flags !== undefined) updateData.flags = JSON.stringify(data.flags);
      if (data.closed_reason !== undefined) updateData.closed_reason = data.closed_reason;
      if (data.cs_owner !== undefined) updateData.cs_owner = data.cs_owner?.trim() || null;
      Object.assign(updateData, buildNurtureUpdatePayload(data));
      if (data.network_pulse_replied !== undefined) updateData.network_pulse_replied = data.network_pulse_replied;
      if (data.network_pulse_replied_at !== undefined) updateData.network_pulse_replied_at = data.network_pulse_replied_at;
      if (data.network_pulse_replied_by !== undefined) updateData.network_pulse_replied_by = data.network_pulse_replied_by;

      if (willBeClosedWon && data.nurture_stage === undefined) {
        updateData.nurture_stage = 'exited_won';
      }

      const { data: updatedDeal, error } = await supabase
        .from('deals')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating deal:', error);
        throw new Error(formatDealPosSystemSchemaError(`Failed to update deal: ${error.message}`));
      }

      if (!updatedDeal) {
        throw new Error('Deal not found');
      }

      // Transform flags from JSONB
      const dealWithFlags = {
        ...updatedDeal,
        flags: Array.isArray(updatedDeal.flags) ? updatedDeal.flags : (updatedDeal.flags ? JSON.parse(updatedDeal.flags) : []),
        sales_state: updatedDeal.sales_state ?? 1,
      } as Deal;

      // Auto-create location order when deal is marked closed_won
      // Only if deal has a location_id (prospects without locations can't create orders)
      if (!wasClosedWon && willBeClosedWon && dealWithFlags.location_id) {
        try {
          // Check if order already exists (idempotency)
          const existingOrders = await locationOrdersService.getByDealId(id);
          if (existingOrders.length === 0) {
            // Create order from deal
            await locationOrdersService.createFromDeal({
              id: dealWithFlags.id,
              location_id: dealWithFlags.location_id,
              total_contract_value: dealWithFlags.total_contract_value,
              name: dealWithFlags.name,
            });
          }
        } catch (error) {
          // Log error but don't fail the deal update
          console.error('Error creating location order from deal:', error);
        }
      }

      return dealWithFlags;
    } catch (error) {
      console.error('Error updating deal:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase.from('deals').delete().eq('id', id);

      if (error) {
        console.error('Error deleting deal:', error);
        throw new Error(formatClosedLostDeleteError(error));
      }
    } catch (error) {
      console.error('Error deleting deal:', error);
      throw error instanceof Error ? error : new Error(formatClosedLostDeleteError(error));
    }
  },

  async deleteClosedLostContact(id: string): Promise<void> {
    const { error } = await supabase.rpc('delete_closed_lost_deal_contact', { p_deal_id: id });
    if (!error) return;

    if (isMissingDeleteRpc(error)) {
      await this.delete(id);
      return;
    }

    throw new Error(formatClosedLostDeleteError(error));
  },

  async listPulseEngagement(): Promise<NetworkPulseDealEngagement[]> {
    const { data, error } = await supabase.rpc('network_pulse_deal_engagement');
    if (error) {
      if (isMissingDeleteRpc(error)) return [];
      throw error;
    }
    return Array.isArray(data) ? data as NetworkPulseDealEngagement[] : [];
  },

  // Pulse engagement score: not identity-bearing (a number and counts tied to
  // a deal_id already fully visible to the caller), so this is a bulk RPC
  // granted directly to authenticated -- same shape as listPulseEngagement
  // above, not the audited service-role Edge Function pattern used for reads
  // that name a contact.
  async listPulseEngagementScores(): Promise<PulseEngagementScore[]> {
    const { data, error } = await supabase.rpc('network_pulse_deal_engagement_score_v2');
    if (error) {
      if (isMissingDeleteRpc(error)) return [];
      throw error;
    }
    return Array.isArray(data) ? data as PulseEngagementScore[] : [];
  },

  async getPulseEngagementScore(dealId: string): Promise<PulseEngagementScore | null> {
    const { data, error } = await supabase.rpc('network_pulse_deal_engagement_score_v2', { p_deal_id: dealId });
    if (error) {
      if (isMissingDeleteRpc(error)) return null;
      throw error;
    }
    const rows = Array.isArray(data) ? data as PulseEngagementScore[] : [];
    return rows[0] ?? null;
  },

  // ============================================
  // WORKFLOW METHODS
  // ============================================

  // Update sales state with validation (soft guidance + hard gates)
  async updateSalesState(dealId: string, newState: SalesState, exceptionNotes?: string): Promise<Deal> {
    const deal = await this.getById(dealId);
    if (!deal) {
      throw new Error('Deal not found');
    }

    // Validate transition (includes hard gate check)
    const validation = validateTransition(deal.sales_state, newState, deal);

    // Hard gate: block State 3→4 transition unless conditions met
    if (validation.hardBlocked) {
      // Check if exception flag exists with notes
      const hasExceptionFlag = deal.flags.some(f => f.type === 'exception' && f.active && f.notes);
      if (!hasExceptionFlag && !exceptionNotes) {
        throw new Error(
          `Cannot transition to State 4: ${validation.reason}. ` +
          `Add Exception Flag with notes to override.`
        );
      }
      // If exception notes provided, add exception flag
      if (exceptionNotes && !hasExceptionFlag) {
        await this.addFlag(dealId, 'exception', exceptionNotes);
      }
    }

    // Soft guidance: warn but allow other invalid transitions
    if (!validation.valid && !validation.hardBlocked && validation.warning) {
      console.warn(validation.warning);
      // Still allow the transition (soft enforcement)
    }

    return this.update(dealId, { sales_state: newState });
  },

  // Add flag
  async addFlag(dealId: string, flagType: DealFlagType, notes?: string): Promise<Deal> {
    const deal = await this.getById(dealId);
    if (!deal) {
      throw new Error('Deal not found');
    }

    // Exception flag requires notes
    if (flagType === 'exception' && !notes) {
      throw new Error('Exception Flag requires notes to document the override');
    }

    const userId = await getCurrentUserId();
    const newFlag: DealFlag = {
      type: flagType,
      active: true,
      created_at: new Date().toISOString(),
      created_by: userId,
      notes: notes || null,
      auto_added: false,
    };

    const updatedFlags = [...deal.flags, newFlag];
    return this.update(dealId, { flags: updatedFlags });
  },

  // Remove flag
  async removeFlag(dealId: string, flagType: DealFlagType): Promise<Deal> {
    const deal = await this.getById(dealId);
    if (!deal) {
      throw new Error('Deal not found');
    }

    const updatedFlags = deal.flags.map(flag =>
      flag.type === flagType ? { ...flag, active: false } : flag
    );

    return this.update(dealId, { flags: updatedFlags });
  },

  // Check and update flags (called after deal updates)
  async refreshFlags(dealId: string): Promise<Deal> {
    const deal = await this.getById(dealId);
    if (!deal) {
      throw new Error('Deal not found');
    }

    let updatedFlags = [...deal.flags];

    // Payment Flag: Check if invoice exists and is unpaid
    const { data: invoices, error: invoicesError } = await supabase
      .from('invoices')
      .select('status')
      .eq('deal_id', dealId);

    if (invoicesError) {
      console.error('Error fetching invoices for deal flags:', invoicesError);
      throw new Error(`Failed to refresh deal flags: ${invoicesError.message}`);
    }

    const hasUnpaidInvoice = (invoices || []).some(
      inv => inv.status !== 'paid' && inv.status !== 'draft'
    );
    if (hasUnpaidInvoice && !updatedFlags.find(f => f.type === 'payment' && f.active)) {
      updatedFlags.push({
        type: 'payment',
        active: true,
        created_at: new Date().toISOString(),
        auto_added: true,
      });
    } else if (!hasUnpaidInvoice) {
      // Remove payment flag if all invoices are paid
      updatedFlags = updatedFlags.filter(f => !(f.type === 'payment' && f.active));
    }

    // Note: Silence flag and regulatory flag auto-add/remove would go here
    // For now, keeping it simple - can be enhanced later

    return this.update(dealId, { flags: updatedFlags });
  },

  // Get deal with derived fields
  async getByIdWithDerived(id: string): Promise<Deal & { derived: ReturnType<typeof computeDerivedFields> }> {
    const deal = await this.getById(id);
    if (!deal) {
      throw new Error('Deal not found');
    }

    const derived = computeDerivedFields(deal);
    return {
      ...deal,
      derived,
    };
  },
};
